import { DevelopmentAreaMap } from 'components/development-area-map';
import { EmptyState } from 'components/empty-state';
import { Map, PolygonArea } from 'components/map';
import { SkeletonPlaceholder } from '@carbon/react';
import { useAtomValue } from 'jotai';
import { loadable } from 'jotai/utils';
import { buildPolygonStyle, findCenter } from 'lib/map';
import NoMapSvg from 'images/srp-illustration-no-map.svg';
import { useMemo } from 'react';
import {
  crmTractFormLoadableAtom,
  createdTractGeometryLoadableAtom,
  mapConfigAtom,
  referenceLocationIdsAtom,
} from './atoms';
import { SubtractPolygon } from './subtract-polygon';
import { CRMTractForm } from './types';

/** How a created tract's geometry is drawn. */
export const TRACT_GEOMETRY_STYLE = buildPolygonStyle({
  color: '#76BFA9',
  fillOpacity: 0.3,
  lineOpacity: 0.8,
  lineWidth: 2,
});

const referenceLocationIdsLoadableAtom = loadable(referenceLocationIdsAtom);

const SurveyAreaMapContent = ({ form }: { form: CRMTractForm }) => {
  const referenceLocationField = useAtomValue(form.referenceLocation);
  const quarterCalls = useAtomValue(form.quarterCalls);
  const referenceLocationIdsLoadable = useAtomValue(
    referenceLocationIdsLoadableAtom
  );
  const referenceLocationIds =
    referenceLocationIdsLoadable.state === 'hasData'
      ? referenceLocationIdsLoadable.data
      : [];

  const geographies = useMemo(() => {
    if (!referenceLocationField?.value) return [];
    return [
      {
        id: referenceLocationField?.value?.id,
        referenceLocation: referenceLocationField?.value,
        quarterCalls: quarterCalls.value,
      },
    ];
  }, [referenceLocationField, quarterCalls.value]);

  return (
    <DevelopmentAreaMap
      geographies={geographies}
      referenceLocationIds={referenceLocationIds}
      mapConfigAtom={mapConfigAtom}
    />
  );
};

export const SurveyAreaMap = () => {
  const formLoadable = useAtomValue(crmTractFormLoadableAtom);

  if (formLoadable.state === 'loading') {
    return <SkeletonPlaceholder />;
  }

  if (formLoadable.state === 'hasError') {
    return (
      <EmptyState
        icon={NoMapSvg}
        headerText="Error loading map"
        helperText="Unable to load map data. Please try again."
      />
    );
  }

  return <SurveyAreaMapContent form={formLoadable.data} />;
};

export const TractMap = () => {
  const tractGeometryLoadable = useAtomValue(createdTractGeometryLoadableAtom);

  const geometry =
    tractGeometryLoadable.state === 'hasData'
      ? tractGeometryLoadable.data?.geojson
      : null;

  const center = useMemo(() => {
    if (!geometry) return null;
    return findCenter(geometry);
  }, [geometry]);

  if (tractGeometryLoadable.state === 'loading') {
    return <SkeletonPlaceholder style={{ width: '100%', height: '100%' }} />;
  }

  if (tractGeometryLoadable.state === 'hasError') {
    return (
      <EmptyState
        icon={NoMapSvg}
        headerText="Error loading map"
        helperText="Unable to load tract geometry. Please try again."
      />
    );
  }

  if (!geometry || !center) {
    return (
      <EmptyState
        icon={NoMapSvg}
        headerText="No Tract Geometry"
        helperText="Tract geometry is not available."
      />
    );
  }

  return (
    <Map
      longitude={center.longitude}
      latitude={center.latitude}
      mapConfigAtom={mapConfigAtom}
      showControls
      showMapFilters
    >
      <PolygonArea
        id="tract-geometry"
        geometry={geometry}
        style={TRACT_GEOMETRY_STYLE}
      />
      <SubtractPolygon />
    </Map>
  );
};
